<template>
    <div :class="`rating-scale rating-scale-align-${align}`">
        <label for="rating">{{ fieldLabel }}</label>
        <div class="scale-container">
        <div class="scale">
                <div 
                    v-for="num in 5" 
                    :key="num"
                    @click="setRating(num)" 
                    :class="['circle', { 'rated': rating === num, 'hovered': hoveredRating === num }]"
                    @mouseenter="setHoveredRating(num)"
                    @mouseleave="clearHoveredRating"
                >
                    <span class="number">{{ num }}</span>
                    <div v-if="num === 1 || num === 5" class="label-container">
                        <p class="scale-label">{{ num === 1 ? subLabelOne : subLabelTwo }}</p>
            </div>
                    
                    <!-- Rated circle için sparkle efekti -->
                    <div v-if="rating === num" class="sparkle-effect">
                        <span class="sparkle"></span>
                        <span class="sparkle"></span>
                        <span class="sparkle"></span>
            </div>
            </div>
            </div>
            
            <!-- Selected rating display -->
            <div class="rating-display" v-if="rating > 0">
                <div class="rating-value">
                    <span class="value">{{ rating }}</span>
                    <span class="divider">/</span>
                    <span class="max">5</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "FormRatingScale",
    props: {
        fieldLabel: {
            type: String,
            required: true,
        },
        align: {
            type: String,
            required: true,
        },
        subLabelOne: {
            type: String,
            required: true,
        },
        subLabelTwo: {
            type: String,
            required: true,
        },
        value: {
            type: [Object, String],
            default: () => ({ rating: 0 })
        },
        formId: {
            type: String,
            required: true,
        },
        order: {
            type: Number,
            required: true,
        }
    },
    data() {
        const safeValue = (typeof this.value === 'object' && this.value !== null) ? this.value : {};
        return {
            rating: safeValue.rating || 0,
            hoveredRating: 0,
        };
    },
    watch: {
        value: {
            handler(newValue) {
                const safeValue = (typeof newValue === 'object' && newValue !== null) ? newValue : {};
                this.rating = safeValue.rating || 0;
            },
            deep: true
        }
    },
    methods: {
        setRating(value) {
            this.rating = value;
            this.emitValues();
        },
        setHoveredRating(value) {
            this.hoveredRating = value;
        },
        clearHoveredRating() {
            this.hoveredRating = 0;
        },
        emitValues() {
            const ratingData = {
                rating: this.rating,
                fieldLabel: this.fieldLabel,
                subLabelOne: this.subLabelOne,
                subLabelTwo: this.subLabelTwo
            };
            
            this.$emit("input", ratingData);
            localStorage.setItem(`${this.formId}-${this.order}`, JSON.stringify(ratingData));
        },
        loadValues() {
            const savedData = localStorage.getItem(`${this.formId}-${this.order}`);
            if (savedData) {
                const parsedData = JSON.parse(savedData);
                this.rating = parsedData.rating || 0;
            }
        }
    },
    mounted() {
        const hasSlugValue = this.value && typeof this.value === "object" && this.value.rating && this.value.rating > 0;

        if (!hasSlugValue) {
            this.loadValues();
        }
        this.emitValues();
    }
};
</script>

<style lang="scss" scoped>
.rating-scale {
    width: 100%;
    margin: 0 auto;
    font-family: var(--font-family);
}

// Align sistem - left
.rating-scale-align-left {
    display: flex;
    align-items: flex-start;
    gap: clamp(0.75rem, 2vw, 1rem);

    > label {
        width: clamp(140px, 20vw, 200px);
        font-size: clamp(0.9rem, 2vw, 1rem);
        font-weight: 500;
        color: var(--text-primary);
        margin: 0;
        line-height: 1.5;
        flex-shrink: 0;
        padding-top: clamp(0.375rem, 1vw, 0.5rem);
    }

    .scale-container {
        flex: 1;
        min-width: 0;
    }
}

// Align sistem - right
.rating-scale-align-right {
    display: flex;
    align-items: flex-start;
    gap: clamp(0.75rem, 2vw, 1rem);

    > label {
        width: clamp(140px, 20vw, 200px);
        font-size: clamp(0.9rem, 2vw, 1rem);
        font-weight: 500;
        color: var(--text-primary);
        text-align: right;
        margin: 0;
        line-height: 1.5;
        flex-shrink: 0;
        padding-top: clamp(0.375rem, 1vw, 0.5rem);
    }

    .scale-container {
        flex: 1;
        min-width: 0;
    }
}

// Top align (default)
.rating-scale:not(.rating-scale-align-left):not(.rating-scale-align-right) {
    > label {
        display: block;
        font-size: clamp(0.9rem, 2vw, 1rem);
        font-weight: 500;
        color: var(--text-primary);
        margin: 0 0 clamp(0.625rem, 1.5vw, 0.75rem) 0;
        line-height: 1.5;
    }
}

// Scale container
.scale-container {
    background: var(--surface-color);
    backdrop-filter: blur(8px);
    border: 2px solid var(--border-color);
    border-radius: clamp(0.75rem, 1.8vw, 1rem);
    padding: clamp(1rem, 3vw, 1.5rem);
    transition: all 0.3s ease;
    
    &:hover {
        border-color: var(--primary);
        box-shadow: 0 8px 25px rgba(99, 102, 241, 0.15);
    }
}

// Scale circles
    .scale {
        display: flex;
        justify-content: space-between;
    gap: clamp(0.75rem, 2vw, 1rem);
    margin-bottom: clamp(1rem, 2.5vw, 1.25rem);

        .circle {
        position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
        width: clamp(3rem, 7.5vw, 3.75rem);
        height: clamp(3rem, 7.5vw, 3.75rem);
        background: var(--surface-color);
        border: 1px solid rgba(209, 213, 219, 0.6);
            border-radius: 50%;
            cursor: pointer;
        transition: all 0.3s ease;
        overflow: visible;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04), 
                    inset 0 1px 2px rgba(255, 255, 255, 0.3);
        
        .number {
            font-size: clamp(1rem, 2.5vw, 1.25rem);
            font-weight: 600;
            color: var(--text-secondary);
            transition: all 0.3s ease;
            z-index: 2;
        }
        
        .label-container {
            position: absolute;
            top: clamp(4rem, 9vw, 4.6875rem);
            left: 50%;
            transform: translateX(-50%);
            white-space: nowrap;
            
            .scale-label {
                font-size: clamp(0.7rem, 1.5vw, 0.75rem);
                color: var(--text-secondary);
                margin: 0;
                text-align: center;
                font-weight: 500;
                line-height: 1.3;
            }
        }
        
        // Hover efektleri
        &:hover,
        &.hovered {
            background: rgba(99, 102, 241, 0.1);
            border-color: var(--primary);
            transform: scale(1.1);
            box-shadow: 0 4px 12px rgba(99, 102, 241, 0.2);
            
            .number {
                color: var(--primary);
                transform: scale(1.1);
            }
            
            .scale-label {
                color: var(--primary);
                font-weight: 600;
            }
        }
        
        // Seçili durumu
        &.rated {
            background: var(--primary);
            border-color: var(--primary);
            box-shadow: 0 6px 20px rgba(99, 102, 241, 0.3);
            
            .number {
                color: white;
                transform: scale(1.1);
            }
            
            .scale-label {
                color: var(--primary);
                font-weight: 700;
            }
            
            &:hover {
                background: var(--primary);
                transform: scale(1.15);
                box-shadow: 0 8px 25px rgba(99, 102, 241, 0.4);
            }
        }
        
        // Sparkle efekti
        .sparkle-effect {
            position: absolute;
            width: 100%;
            height: 100%;
            pointer-events: none;
            
            .sparkle {
                position: absolute;
                width: 3px;
                height: 3px;
                background: white;
                border-radius: 50%;
                
                &:nth-child(1) {
                    top: 15px;
                    right: 18px;
                    animation: sparkle-1 2s infinite;
                }
                
                &:nth-child(2) {
                    top: 20px;
                    left: 15px;
                    animation: sparkle-2 2s infinite 0.5s;
                }

                &:nth-child(3) {
                    bottom: 18px;
                    right: 22px;
                    animation: sparkle-3 2s infinite 1s;
                }
            }
        }
    }
}

// Rating display
.rating-display {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: clamp(0.5rem, 1.5vw, 0.75rem);
    padding-top: clamp(0.75rem, 2vw, 1rem);
    border-top: 1px solid var(--border-color);
    
    .rating-value {
        display: flex;
        align-items: baseline;
        gap: clamp(0.25rem, 0.5vw, 0.375rem);
        font-weight: 600;
        
        .value {
            font-size: clamp(1.25rem, 3vw, 1.5rem);
            color: var(--primary);
            text-shadow: 0 1px 2px rgba(99, 102, 241, 0.3);
        }
        
        .divider {
            font-size: clamp(1rem, 2.2vw, 1.125rem);
            color: var(--text-secondary);
        }
        
        .max {
            font-size: clamp(1rem, 2.2vw, 1.125rem);
            color: var(--text-secondary);
        }
    }
    
    .rating-text {
        font-size: clamp(0.8rem, 1.8vw, 0.875rem);
        color: var(--text-secondary);
        font-weight: 500;
    }
}

// Sparkle animasyonları
@keyframes sparkle-1 {
    0%, 100% {
        opacity: 0;
        transform: scale(0);
    }
    50% {
        opacity: 1;
        transform: scale(1);
    }
}

@keyframes sparkle-2 {
    0%, 100% {
        opacity: 0;
        transform: scale(0) rotate(0deg);
    }
    50% {
        opacity: 1;
        transform: scale(1.2) rotate(180deg);
    }
}

@keyframes sparkle-3 {
    0%, 100% {
        opacity: 0;
        transform: scale(0);
    }
    50% {
        opacity: 1;
        transform: scale(0.8);
    }
}

// Mobil optimizasyonları
@media (max-width: 768px) {
    .rating-scale-align-left,
    .rating-scale-align-right {
        flex-direction: column;
        gap: 0;
        
        > label {
            width: 100%;
            text-align: left !important;
            padding-top: 0;
            margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
        }
        
        .scale-container {
            width: 100%;
        }
    }
    
    .scale-container {
        padding: clamp(0.875rem, 2.5vw, 1.25rem);
        border-radius: clamp(0.5rem, 1.5vw, 0.75rem);
    }
    
    .scale {
        gap: clamp(0.5rem, 1.5vw, 0.75rem);
        margin-bottom: clamp(0.75rem, 2vw, 1rem);
        
        .circle {
            width: clamp(2.75rem, 6.5vw, 3.125rem);
            height: clamp(2.75rem, 6.5vw, 3.125rem);
            
            &:hover {
                transform: scale(1.05);
            }
            
            .number {
                font-size: clamp(0.875rem, 2.2vw, 1.125rem);
            }
            
            .label-container {
                top: clamp(3.5rem, 8vw, 3.75rem);
                
                .scale-label {
                    font-size: clamp(0.65rem, 1.4vw, 0.7rem);
                }
            }
            
            .sparkle-effect .sparkle {
                width: 2px;
                height: 2px;
            }
        }
    }
    
    .rating-display {
        gap: clamp(0.375rem, 1vw, 0.5rem);
        padding-top: clamp(0.625rem, 1.5vw, 0.75rem);
        
        .rating-value {
            gap: clamp(0.125rem, 0.4vw, 0.25rem);
            
            .value {
                font-size: clamp(1.125rem, 2.5vw, 1.25rem);
            }
            
            .divider,
            .max {
                font-size: clamp(0.875rem, 2vw, 1rem);
            }
        }
        
        .rating-text {
            font-size: clamp(0.75rem, 1.6vw, 0.8rem);
        }
    }
}

// Küçük ekranlar için
@media (max-width: 480px) {
    .rating-scale-align-left,
    .rating-scale-align-right {
        > label {
            font-size: 0.875rem;
            margin-bottom: 0.5rem;
        }
    }
    
    .rating-scale:not(.rating-scale-align-left):not(.rating-scale-align-right) {
        > label {
            font-size: 0.875rem;
            margin-bottom: 0.625rem;
        }
    }
    
    .scale-container {
        padding: 0.875rem;
        border-radius: 0.5rem;
    }
    
    .scale {
        gap: 0.375rem;
        flex-wrap: wrap;
        justify-content: center;
        margin-bottom: 0.75rem;
        
        .circle {
            width: 2.75rem;
            height: 2.75rem;
            
            &:hover {
                transform: scale(1.03);
            }
            
            &:active {
                transform: scale(0.97);
            }
            
            .number {
                font-size: 0.875rem;
            }
            
            .label-container {
                top: 3.4375rem;
                
                .scale-label {
                    font-size: 0.625rem;
                }
            }
            
            .sparkle-effect .sparkle {
                width: 2px;
                height: 2px;
                
                &:nth-child(1) {
                    top: 10px;
                    right: 12px;
                }
                
                &:nth-child(2) {
                    top: 12px;
                    left: 10px;
                }
                
                &:nth-child(3) {
                    bottom: 12px;
                    right: 14px;
                }
            }
        }
    }
    
    .rating-display {
        flex-direction: column;
        gap: 0.375rem;
        padding-top: 0.625rem;
        
        .rating-value {
            gap: 0.125rem;
            
            .value {
                font-size: 1.125rem;
            }
            
            .divider,
            .max {
                font-size: 0.875rem;
            }
        }
        
        .rating-text {
            font-size: 0.75rem;
        }
    }
}
</style>